// src/admin/pages/ProductsAdminPage.tsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Stack,
  Alert,
  Chip,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useQuery, useMutation } from "@apollo/client";
import {
  ADMIN_PRODUCTS_QUERY,
  DELETE_PRODUCT_MUTATION,
} from "../../graphql/products";
import AdminLoading from "../Components/AdminLoading";
import ConfirmDialog from "../Components/ConfirmDialog";
import { useNavigate } from "react-router-dom";

type Product = {
  id: string;
  name: string;
  productType: string;
  metal: string;
  shortDescription?: string | null;
  longDescription?: string | null;
  price: number;
  bestSeller: boolean;
  rotationImages: string[];
  galleryImages: string[];
};

const ProductsAdminPage: React.FC = () => {
  const navigate = useNavigate();

  const { data, loading, error } = useQuery(ADMIN_PRODUCTS_QUERY);

  const [deleteProduct, { loading: deleting }] = useMutation(
    DELETE_PRODUCT_MUTATION,
    { refetchQueries: [{ query: ADMIN_PRODUCTS_QUERY }] }
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Product | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const products: Product[] = data?.products || [];

  const filtered = useMemo(
    () =>
      products.filter((p) =>
        (p.name + " " + p.productType)
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [products, search]
  );

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const busy = loading || deleting;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const startCreate = () => {
    navigate("/admin/products/new");
  };

  const startEdit = (product: Product) => {
    navigate(`/admin/products/${product.id}`);
  };

  const askDelete = (product: Product) => {
    setToDelete(product);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setActionError(null);
    try {
      await deleteProduct({ variables: { id: toDelete.id } });
      setConfirmOpen(false);
      setToDelete(null);
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  return (
    <>
      <AdminLoading open={busy} />
      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          mb={2}
          spacing={2}
        >
          <Typography variant="h5">Products</Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={startCreate}
            >
              New Product
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load products: {error.message}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Metal</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Best Seller</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No products found.
                    </TableCell>
                  </TableRow>
                )}

                {paged.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.productType}</TableCell>
                    <TableCell>{product.metal}</TableCell>
                    <TableCell>{product.price.toFixed(2)}</TableCell>
                    <TableCell>
                      {product.bestSeller ? (
                        <Chip label="Yes" color="success" size="small" />
                      ) : (
                        <Chip label="No" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => startEdit(product)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => askDelete(product)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title="Delete product?"
        description={
          toDelete ? `Are you sure you want to delete "${toDelete.name}"?` : ""
        }
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default ProductsAdminPage;
